import { Box } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, Fragment, useEffect, useMemo, useRef, useState } from "react";
import { addRecurringShiftMultiSizePopupKey } from "../../services/LocalStorageService";
import { ChangesSource, type DepartmentLocation, type RecurringShift } from "../../utils/models";
import { RecurringShiftMasterSchedule } from "../RecurringShift/RecurringShiftMasterSchedule/RecurringShiftMasterSchedule";
import { SystemTabs } from "../UIToolkit/Clickable/TabsAndChips/SystemTabs/SystemTabs";
import type { SystemTab } from "../UIToolkit/Clickable/TabsAndChips/SystemTabs/utils";
import SystemDialog from "../UIToolkit/Popups/Dialog";
import { MultiSizePopup } from "../UIToolkit/Popups/MultiSizePopup/MultiSizePopup";
import { MultiSizePopupState } from "../UIToolkit/Popups/MultiSizePopup/utils";
import { MedicalDepartmentPostShift } from "./MedicalDepartmentPostShift/MedicalDepartmentPostShift";
import { SynergyMinimalShiftAddition } from "./SynergyMinimalShiftAddition/SynergyMinimalShiftAddition";

type Props = {
	mode: "add-recurring" | "add-single";
	facility?: DepartmentLocation;
	clickedDate: DateTime;
	onClose?: ((didUpdateOrCreate?: boolean) => void) | ((didUpdateOrCreate?: boolean) => Promise<void>);
	hideCreateShiftsTab?: boolean;
	/** Called once when the user first makes a change inside this popup */
	onDidChange?: () => void;
	clickedNurseId?: never;
	existingRecurringShiftId?: never;
	recurringShiftsData?: never;
};

type EditRecurringProps = Omit<Props, "mode" | "onDidChange" | "clickedNurseId" | "existingRecurringShiftId" | "recurringShiftsData"> & {
	mode: "edit-recurring";
	clickedNurseId?: number;
	existingRecurringShiftId?: number;
	recurringShiftsData?: RecurringShift[];
	onDidChange?: never;
};

type MedicalAdditionsTabsType = "add-to-shift" | "create-shift";

const tabsData = (hideCreateShiftsTab: boolean) => {
	const tabs: SystemTab<MedicalAdditionsTabsType>[] = [
		{
			label: "Add to Shift",
			value: "add-to-shift",
		},
	];

	if (!hideCreateShiftsTab) {
		tabs.push({
			label: "Create Shift",
			value: "create-shift" as const,
		});
	}

	return tabs;
};

const tabTypeToStringMap: Record<MedicalAdditionsTabsType, string> = {
	"add-to-shift": "Add staff",
	"create-shift": "Post Shift",
};

export const MedicalDepartmentAdditionsPopup: FC<Props | EditRecurringProps> = ({
	mode,
	facility,
	clickedDate,
	onClose,
	hideCreateShiftsTab = false,
	onDidChange,
	clickedNurseId,
	existingRecurringShiftId,
	recurringShiftsData,
}) => {
	const [showWarningClose, setShowWarningClose] = useState<boolean>(false);
	const [showSwitchTabWarning, setShowSwitchTabWarning] = useState<MedicalAdditionsTabsType | null>(null);
	const [selectedTab, setSelectedTab] = useState<MedicalAdditionsTabsType>("add-to-shift");

	// Keep a stable ref to the latest onDidChange callback so the tracked ref closure stays fresh
	const onDidChangeRef = useRef(onDidChange);
	useEffect(() => {
		onDidChangeRef.current = onDidChange;
	}, [onDidChange]);

	const didChangeRef = useMemo(() => {
		// Custom ref-like object that calls onDidChange when first set to true
		let _value = false;
		return {
			get current(): boolean {
				return _value;
			},
			set current(val: boolean) {
				const wasTrue = _value;
				_value = val;
				if (!wasTrue && val) {
					onDidChangeRef.current?.();
				}
			},
		};
	}, []);
	const warnOnClose = () => {
		if (didChangeRef.current) {
			setShowWarningClose(true);
			return true;
		}
		return false;
	};

	const warnOnTabSwitch = (tabToSwitch: MedicalAdditionsTabsType) => {
		if (didChangeRef.current) {
			setShowSwitchTabWarning(tabToSwitch);
			return true;
		}
	};

	const handleCloseClick = (didUpdateOrCreate?: boolean) => {
		if (didUpdateOrCreate) {
			onClose?.(didUpdateOrCreate);
		} else if (!warnOnClose()) {
			onClose?.(false);
		}
	};

	const handleTabChange = (newTab: MedicalAdditionsTabsType) => {
		if (newTab !== selectedTab) {
			if (warnOnTabSwitch(newTab)) {
				return;
			}
			setSelectedTab(newTab);
		}
	};

	const isEditRecurring = mode === "edit-recurring";
	const allowMaximize = isEditRecurring || (mode === "add-recurring" && selectedTab === "add-to-shift");

	const minimizedBody = isEditRecurring ? null : selectedTab === "add-to-shift" ? (
		<SynergyMinimalShiftAddition
			clickedDate={clickedDate}
			mode={mode}
			facility={facility}
			didChangeRef={didChangeRef}
			onWarnOnClose={warnOnClose}
			onClose={handleCloseClick}
		/>
	) : (
		<MedicalDepartmentPostShift
			clickedDate={clickedDate}
			facility={facility}
			didChangeRef={didChangeRef}
			onWarnOnClose={warnOnClose}
			onClose={handleCloseClick}
		/>
	);

	const bodyRenderer = (currentSizeState: MultiSizePopupState) => {
		const isHidden = currentSizeState === MultiSizePopupState.HIDDEN;
		const isMaximized = currentSizeState === MultiSizePopupState.MAXIMIZED;
		const showMaximizedRecurring = allowMaximize && isMaximized;
		const tabs = tabsData(hideCreateShiftsTab);

		return (
			<>
				{allowMaximize && (
					<Box
						sx={{
							display: showMaximizedRecurring ? "block" : "none",
						}}
					>
						<RecurringShiftMasterSchedule
							clickedDate={clickedDate}
							clickedLocation={facility}
							shouldAddNewShiftOnOpen={!isEditRecurring}
							onClose={handleCloseClick}
							disableHidePopup={true}
							renderWithoutModal={true}
							clickedNurseId={clickedNurseId}
							knownClickedRecurringShiftId={existingRecurringShiftId}
							recurringShiftsData={recurringShiftsData}
							source={ChangesSource.MasterSchedule}
						/>
					</Box>
				)}
				{!isEditRecurring && (
					<Box
						sx={{
							display: showMaximizedRecurring ? "none" : "block",
							visibility: isHidden ? "hidden" : "visible",
							pointerEvents: isHidden ? "none" : "auto",
							height: isHidden ? 0 : "auto",
							overflow: "hidden",
						}}
					>
						{tabs.length > 1 && (
							<Box
								sx={{
									px: 1,
								}}
							>
								<SystemTabs tabs={tabs} currentTab={selectedTab} onTabChange={handleTabChange} />
							</Box>
						)}
						{minimizedBody}
					</Box>
				)}
			</>
		);
	};

	return (
		<Fragment>
			{showWarningClose && (
				<SystemDialog
					open={true}
					onClose={() => setShowWarningClose(false)}
					title={"You have unsaved changes!"}
					body={"Are you sure you want to discard them?"}
					onConfirm={(_) => onClose?.(false)}
					confirm={"Discard Changes"}
					cancel={"Keep Editing"}
				/>
			)}
			{!!showSwitchTabWarning && (
				<SystemDialog
					open={true}
					onClose={() => setShowSwitchTabWarning(null)}
					title={"You have unsaved changes!"}
					body={"Are you sure you want to discard them?"}
					onConfirm={(_) => {
						setSelectedTab(showSwitchTabWarning);
						setShowSwitchTabWarning(null);
						didChangeRef.current = false;
					}}
					confirm={"Discard Changes"}
					cancel={"Keep Editing"}
				/>
			)}
			<MultiSizePopup
				keepBodyMounted={true}
				disableMaximize={!allowMaximize}
				disableMinimize={isEditRecurring}
				popupLocalStorageKey={allowMaximize && !isEditRecurring ? addRecurringShiftMultiSizePopupKey : undefined}
				title={isEditRecurring ? "Recurring Shift" : tabTypeToStringMap[selectedTab]}
				open={true}
				bodyRenderer={bodyRenderer}
				onClose={(_) => handleCloseClick()}
			/>
		</Fragment>
	);
};
